import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Database, Eye, Sparkles, 
  X, Image, 
  Activity,
  TrendingUp, Star, Wand2,
  Zap as Lightning,
  RefreshCw, Archive, RotateCcw, Trash2
} from 'lucide-react';
import axios from 'axios';
import hybridImageService from './hybridImageService';
import './App.css';

// Environment-aware API configuration
const API_BASE = process.env.NODE_ENV === 'production' 
  ? 'https://renovate-library-service-jo572.ondigitalocean.app'
  : 'http://localhost:8000';

// Test API configuration
const TEST_API_BASE = 'https://api.tech.renovateai.app';
let TEST_API_ENDPOINT = '/public/v2/materials/test';
const TEST_API_KEY = 'key_edc77228-a6fb-4172-ab7f-845c4f342d64';

console.log('API Base URL:', API_BASE, '(Environment:', process.env.NODE_ENV, ')');

const RenovateAI = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('renovateActiveTab');
    return savedTab || 'workshop';
  });
  const [materials, setMaterials] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({});
  const [theme] = useState('light');
  const [viewMode, setViewMode] = useState('grid');
  const [generatedImages, setGeneratedImages] = useState([]);
  const [inspirationImages, setInspirationImages] = useState([]); // NEW
  const [lastGeneratedPrompt, setLastGeneratedPrompt] = useState(''); // Store the last generated prompt at parent level
  const [currentGenerationPrompt, setCurrentGenerationPrompt] = useState(''); // Store the current generation prompt

  // Handler functions for search and filter
  const showNotification = React.useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    const notification = { id, message, type };
    setNotifications(prev => [...prev, notification]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  // Initialize theme on component mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.className = `theme-${theme}`;
  }, [theme, showNotification]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('renovateActiveTab', activeTab);
  }, [activeTab]);



  // Test API connection on load (silent)
  useEffect(() => {
    const testConnection = async () => {
      try {
        console.log('Testing API connection to:', API_BASE);
        const response = await axios.get(`${API_BASE}/health`);
        console.log('API connection successful:', response.data);
      } catch (error) {
        console.error('API connection failed:', error);
        showNotification('Backend connection failed - check if service is running', 'error');
      }
    };

    testConnection();
  }, [showNotification]);

  // Load initial data (only once on mount)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('Loading initial stats from:', `${API_BASE}/api/stats`);
        const response = await axios.get(`${API_BASE}/api/stats`);
        console.log('Stats response:', response);
        console.log('Stats data:', response.data);
        setStats(response.data);
        console.log('Stats loaded successfully:', response.data);
      } catch (error) {
        console.error('Error loading stats:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Set default stats to prevent display issues
        setStats({
          total_items: 0,
          catalog_images: 0,
          inspiration_hub_images: 0,
          archived_items: 0,
          by_category: {},
          catalog_materials: 0,
          inspiration_images: 0
        });
        showNotification('Failed to load statistics', 'error');
      }
    };



    const loadInitialMaterials = async () => {
      setIsLoading(true);
      try {
        console.log('Loading all materials (no limit param)');
        const response = await axios.get(`${API_BASE}/api/catalog/materials`);
        setMaterials(response.data.items || []);
        console.log('Materials loaded:', response.data.items?.length || 0, 'items');
      } catch (error) {
        console.error('Error loading materials:', error);
        showNotification('Failed to load materials', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
    loadInitialMaterials();
  }, [showNotification]); // Empty dependency array - only run once on mount

  // Load inspiration images - Updated to use public API endpoint
  const loadInspirationImages = React.useCallback(async (filters = {}) => {
    try {
      // Try public API first, fallback to inspiration-hub API
      const params = { 
        limit: 100, // Get more images for company website
        _t: Date.now(), // Cache-busting parameter
        ...filters // Allow filtering by category, subcategory, search, etc.
      };
      
      let response;
      let images = [];
      
      try {
        // Try public API first
        response = await axios.get(`${API_BASE}/api/public/inspiration/images`, { params });
        images = response.data.data || [];
        console.log('Loaded inspiration images:', images.length, 'from public API');
      } catch (publicError) {
        console.log('Public API failed, trying inspiration-hub API:', publicError.message);
        // Fallback to inspiration-hub API
        response = await axios.get(`${API_BASE}/api/inspiration-hub/images`, { params });
        images = response.data.inspiration_images || response.data.items || [];
        console.log('Loaded inspiration images:', images.length, 'from inspiration-hub API');
      }
      
      setInspirationImages(images);
    } catch (error) {
      console.error('Error loading inspiration images:', error);
      showNotification('Failed to load inspiration images', 'error');
    }
  }, [showNotification]);

  // Load on mount
  useEffect(() => {
    loadInspirationImages();
  }, [loadInspirationImages]);

  // Separate effect for filters and search changes
  useEffect(() => {
    const loadFilteredMaterials = async () => {
      setIsLoading(true);
      try {
        // Don't send status filter to backend when it's 'available' - we'll handle it client-side
        const params = {
          ...(filters.category && { category: filters.category }),
          ...(searchQuery && { search: searchQuery })
        };
        console.log('Loading filtered materials (no limit param):', params);
        const response = await axios.get(`${API_BASE}/api/catalog/materials`, { params });
        setMaterials(response.data.items || []);
        console.log('Filtered materials loaded:', response.data.items?.length || 0, 'items');
      } catch (error) {
        console.error('Error loading filtered materials:', error);
        showNotification('Failed to load filtered materials', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadFilteredMaterials();
  }, [filters, searchQuery, showNotification]);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      showNotification('Failed to load statistics', 'error');
    }
  };

  const loadMaterials = async (page = 1, customFilters = null, customSearch = null) => {
    setIsLoading(true);
    try {
      const filtersToUse = customFilters || filters;
      // Don't send status filter to backend when it's 'available' - we'll handle it client-side
      const params = {
        ...(filtersToUse.category && { category: filtersToUse.category }),
        ...(customSearch && { search: customSearch })
      };
      console.log('Loading all materials (no limit param):', params);
      const response = await axios.get(`${API_BASE}/api/catalog/materials`, { params });
      setMaterials(response.data.items || []);
      console.log('Materials loaded:', response.data.items?.length || 0, 'items');
    } catch (error) {
      console.error('Error loading materials:', error);
      showNotification('Failed to load materials', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler functions for search and filter
  const handleSearch = (searchValue) => {
    setSearchQuery(searchValue);
  };

  const handleFilter = (newFilters) => {
    // Always set filters with empty string for 'all'
    setFilters({
      category: typeof newFilters.category === 'string' ? newFilters.category : '',
      status: typeof newFilters.status === 'string' ? newFilters.status : ''
    });
  };

  const removeNotification = (notificationId) => {
    console.log('Removing notification with ID:', notificationId);
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      console.log('Before filter:', prev.length, 'After filter:', updated.length);
      return updated;
    });
  };

  const generateImages = async (formData) => {
    try {
      setIsGeneratingImages(true);
      console.log('Generating material swatch images with data:', formData);
      console.log('Using frontend Gemini generator (no backend generate call)');

      // Use frontend generation service (Gemini-only) to create images
      const response = await hybridImageService.generateWorkshopImages(formData);
      console.log('Frontend generation result:', response);

      const images = response.images || [];
      if (images.length === 0) {
        let detailedError = 'No images were generated';
        throw new Error(detailedError);
      }
      
      // Enhance the generated images with form data (subcategory, keywords, etc.)
      const enhancedImages = images.map(image => ({
        ...image,
        subcategory: formData.subtype || "",  // Add subcategory from form data
        tags: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(k => k) : []
      }));
      
      // Create enhanced response data
      const enhancedResponse = {
        ...response,
        images: enhancedImages
      };
      
      showNotification(`Generated ${enhancedImages.length} images successfully!`, 'success');
      return enhancedResponse;
    } catch (error) {
      console.error('Full error details:', error);
      
      let errorMessage = 'Failed to generate material swatch images';
      errorMessage = error.message || errorMessage;
      
      showNotification(`${errorMessage}`, 'error');
      throw error;
    } finally {
      setIsGeneratingImages(false);
    }
  };





  const saveMaterial = async (materialData) => {
    try {
      setIsLoading(true);
      console.log('Saving material with data:', materialData);
      console.log('Using API endpoint:', `${API_BASE}/api/workshop/save`);
      
      // Add more detailed logging
      console.log('Material data being sent:', JSON.stringify(materialData, null, 2));
      
      const response = await axios.post(`${API_BASE}/api/workshop/save`, materialData);
      console.log('Saved material:', response.data);
      
      showNotification(`Material "${response.data.name}" saved!`, 'success');
      loadStats();
      loadMaterials(1); // Reload current page
      return response.data;
    } catch (error) {
      console.error('Error saving material:', error);
      console.error('Error response:', error.response);
      console.error('Error request:', error.request);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to save material';
      if (error.response) {
        console.error('Server response data:', error.response.data);
        console.error('Server response status:', error.response.status);
        console.error('Server response headers:', error.response.headers);
        errorMessage = error.response.data?.detail || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Cannot connect to backend server. Please check if the service is running.';
      } else {
        errorMessage = error.message;
      }
      
      showNotification(`${errorMessage}`, 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Archive material function
  const archiveMaterial = async (materialId, materialName) => {
    try {
      console.log('Archiving material:', materialId);
      const response = await axios.patch(`${API_BASE}/api/catalog/materials/${materialId}/archive`);
      console.log('Material archived:', response.data);
      
      showNotification(`Material "${materialName}" archived!`, 'success');
      loadStats();
      loadMaterials(1); // Reload current page
      return response.data;
    } catch (error) {
      console.error('Error archiving material:', error);
      showNotification(`Failed to archive material`, 'error');
      throw error;
    }
  };

  // NEW: Restore material function
  const restoreMaterial = async (materialId, materialName) => {
    try {
      console.log('Restoring material:', materialId);
      const response = await axios.patch(`${API_BASE}/api/catalog/materials/${materialId}/restore`);
      console.log('Material restored:', response.data);
      
      showNotification(`Material "${materialName}" restored!`, 'success');
      loadStats();
      loadMaterials(1); // Reload current page
      return response.data;
    } catch (error) {
      console.error('❌ Error restoring material:', error);
      showNotification(`❌ Failed to restore material`, 'error');
      throw error;
    }
  };

  // NEW: Delete material function
  const deleteMaterial = async (materialId, materialName) => {
    try {
      console.log('🗑️ Deleting material:', materialId);
      const response = await axios.delete(`${API_BASE}/api/catalog/materials/${materialId}`);
      console.log('✅ Material deleted:', response.data);
      
      showNotification(`🗑️ Material "${materialName}" permanently deleted!`, 'success');
      loadStats();
      loadMaterials(1); // Reload current page
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting material:', error);
      showNotification(`❌ Failed to delete material`, 'error');
      throw error;
    }
  };
    




  // --- Save to Inspiration Hub ---
  const saveInspirationImage = async (inspirationData) => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API_BASE}/api/inspiration-hub/images`, inspirationData);
      showNotification('Inspiration image saved to hub!', 'success');
      loadInspirationImages();
      loadStats(); // Update header stats after saving
      return response.data;
    } catch (error) {
      console.error('Error saving inspiration image:', error);
      showNotification('Failed to save inspiration image', 'error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ... existing code ...

  // --- Inspiration archive/delete functions ---
  const archiveInspiration = async (inspirationId, inspirationName) => {
    try {
      const res = await fetch(`${API_BASE}/api/inspiration-hub/images/${inspirationId}/archive`, { method: 'PATCH' });
      const data = await res.json();
      if (data.archived) {
        const updated = inspirationImages.map(img => img.id === inspirationId ? { ...img, archived: true } : img);
        setInspirationImages(updated);
        showNotification(`Inspiration "${inspirationName}" archived!`, 'success');
        loadStats(); // Update header stats after archiving
      } else {
        showNotification(`Failed to archive inspiration.`, 'error');
      }
    } catch (err) {
      showNotification(`Failed to archive inspiration.`, 'error');
    }
  };

  const deleteInspiration = async (inspirationId, inspirationName) => {
    try {
      const res = await fetch(`${API_BASE}/api/inspiration-hub/images/${inspirationId}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      if (data.deleted) {
        const updated = inspirationImages.filter(img => img.id !== inspirationId);
        setInspirationImages(updated);
        showNotification(`Inspiration "${inspirationName}" deleted!`, 'success');
        loadStats(); // Update header stats after deleting
      } else {
        showNotification(`Failed to delete inspiration.`, 'error');
      }
    } catch (err) {
      showNotification(`Failed to delete inspiration.`, 'error');
    }
  };

  // --- InspirationHub Component ---
  const InspirationHub = ({ images, isLoading, showNotification, setInspirationImages, loadStats }) => {
    // --- Match Catalog UI/UX ---
    const [searchQuery, setSearchQuery] = useState('');
    const [localFilters, setLocalFilters] = useState({
      category: '',
      status: '',
      colorTag: ''
    });
    const [sortBy, setSortBy] = useState('newest');
    const [inspirationList, setInspirationList] = useState(images);

    useEffect(() => {
      setInspirationList(images);
    }, [images]);

    const handleSearch = (value) => {
      setSearchQuery(value);
    };

    const showAllInspirations = () => {
      setLocalFilters({ category: '', status: '', colorTag: '' });
      setSearchQuery('');
    };

    const handleFilterChange = (key, value) => {
      if ((key === 'category' || key === 'status' || key === 'colorTag') && value === '') {
        showAllInspirations();
        return;
      }
      const newFilters = { ...localFilters, [key]: value };
      setLocalFilters(newFilters);
    };

    const handleClearFilters = showAllInspirations;

    // Available color tags for filtering
    const availableColorTags = [
      "blacks", "whites", "grays", "blues", "greens", "reds", "yellows", "oranges", "purples", "browns", "neutrals",
      "pastels", "gold", "silver", "pinks"
    ];

    // Sort and filter inspiration images
    const sortedInspirations = React.useMemo(() => {
      const arr = [...inspirationList];
      if (sortBy === 'newest') {
        arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else if (sortBy === 'oldest') {
        arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      } else if (sortBy === 'name') {
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } else if (sortBy === 'category') {
        arr.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
      }
      return arr;
    }, [inspirationList, sortBy]);

    const filteredInspirations = sortedInspirations.filter(img => {
      // Status filter - handle archived as string from backend
      const isArchived = img.archived === "true" || img.archived === true;
      const statusMatch = localFilters.status === '' ||
        (localFilters.status === 'available' && (!isArchived)) ||
        (localFilters.status === 'archived' && isArchived);
      // Category filter
      const categoryMatch = localFilters.category === '' || img.category === localFilters.category;
      // Color tag filter
      const colorTagMatch = localFilters.colorTag === '' || 
        (img.color_tags && img.color_tags.includes(localFilters.colorTag));
      // Search filter
      const searchMatch =
        (img?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (img?.prompt || '').toLowerCase().includes(searchQuery.toLowerCase());
      return statusMatch && categoryMatch && colorTagMatch && searchMatch;
    });

    // --- Archive/Delete logic remains unchanged ---
    const handleArchive = async (e, inspiration) => {
      e.stopPropagation();
      if (window.confirm(`Are you sure you want to archive "${inspiration.name}"?`)) {
        try {
          const res = await fetch(`${API_BASE}/api/inspiration-hub/images/${inspiration.id}/archive`, { method: 'PATCH' });
          const data = await res.json();
          if (data.archived) {
            const updated = inspirationList.map(img => img.id === inspiration.id ? { ...img, archived: "true" } : img);
            setInspirationList(updated);
            if (setInspirationImages) setInspirationImages(updated);
            if (showNotification) showNotification(`Inspiration "${inspiration.name}" archived!`, 'success');
            if (loadStats) loadStats(); // Update header stats after archiving
          } else {
            if (showNotification) showNotification(`Failed to archive inspiration.`, 'error');
          }
        } catch (err) {
          if (showNotification) showNotification(`Failed to archive inspiration.`, 'error');
        }
      }
    };

    const handleDelete = async (e, inspiration) => {
      e.stopPropagation();
      if (window.confirm(`⚠️ Are you sure you want to PERMANENTLY DELETE "${inspiration.name}"? This action cannot be undone!`)) {
        try {
          const res = await fetch(`${API_BASE}/api/inspiration-hub/images/${inspiration.id}`, { method: 'DELETE' });
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          }
          const data = await res.json();
          if (data.deleted) {
            // Remove from local state immediately for better UX
            const updated = inspirationList.filter(img => img.id !== inspiration.id);
            setInspirationList(updated);
            if (setInspirationImages) setInspirationImages(updated);
            if (showNotification) showNotification(`Inspiration "${inspiration.name}" deleted!`, 'success');
            if (loadStats) loadStats(); // Update header stats after deleting
            
            // Don't force refresh immediately - let the user refresh manually if needed
            // The local state update should be sufficient for most cases
            console.log('✅ Image deleted from local state. Server deletion confirmed.');
            
            // Optional: Add a small indicator that deletion was successful
            setTimeout(() => {
              if (showNotification) showNotification('Image deleted successfully!', 'success');
            }, 100);
          } else {
            if (showNotification) showNotification(`Failed to delete inspiration.`, 'error');
          }
        } catch (err) {
          if (showNotification) showNotification(`Failed to delete inspiration.`, 'error');
        }
      }
    };

    // NEW: Handle restore action
    const handleRestore = async (e, inspiration) => {
      e.stopPropagation();
      try {
        const res = await fetch(`${API_BASE}/api/inspiration-hub/images/${inspiration.id}/restore`, { method: 'PATCH' });
        if (res.ok) {
          const data = await res.json(); // Consume the response
          if (data.restored) {
            const updated = inspirationList.map(img => img.id === inspiration.id ? { ...img, archived: "false" } : img);
            setInspirationList(updated);
            if (setInspirationImages) setInspirationImages(updated);
            if (showNotification) showNotification(`Inspiration "${inspiration.name}" restored!`, 'success');
            if (loadStats) loadStats(); // Update header stats after restoring
          } else {
            if (showNotification) showNotification(`Failed to restore inspiration.`, 'error');
          }
        } else {
          if (showNotification) showNotification(`Failed to restore inspiration.`, 'error');
        }
      } catch (err) {
        if (showNotification) showNotification(`Failed to restore inspiration.`, 'error');
      }
    };

    return (
      <div className="catalog modern-catalog">
        <div className="catalog-header">
          <div className="catalog-title">
            <h2>✨ Inspiration Hub</h2>
            <p>Browse and manage your inspiration images for PR, marketing, and design ideas.</p>
          </div>
          
          <div className="catalog-controls">
            <div className="search-section">
              <div className="search-bar modern-search">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search inspiration images..."
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                />
              </div>
            </div>
            
            <div className="filter-section">
              <select
                value={localFilters.category}
                onChange={e => handleFilterChange('category', e.target.value)}
                className="modern-filter"
              >
                <option value="">All Categories</option>
                <option value="interior">🏠 Interior</option>
                <option value="exterior">🏗️ Exterior</option>
              </select>
              <select
                value={localFilters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                className="modern-filter"
              >
                <option value="">All Status</option>
                <option value="available">✅ Available</option>
                <option value="archived">📦 Archived</option>
              </select>
              <select
                value={localFilters.colorTag}
                onChange={e => handleFilterChange('colorTag', e.target.value)}
                className="modern-filter"
              >
                <option value="">All Color Tags</option>
                {availableColorTags.map(tag => (
                  <option key={tag} value={tag}>🎨 {tag}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="modern-filter"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Name A-Z</option>
                <option value="category">Category</option>
              </select>

              {/* Clear Filters Button */}
              {(localFilters.category || localFilters.status || localFilters.colorTag || searchQuery) && (
                <button
                  className="modern-filter"
                  style={{ minWidth: 100, background: '#eee', color: '#333', marginLeft: 8 }}
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner-large" />
            <p>Loading inspiration images...</p>
          </div>
        ) : filteredInspirations.length === 0 ? (
          <div className="empty-state-modern">
            <Sparkles size={64} />
            <h3>No inspiration images found</h3>
            <p>Generate and save some inspiration images!</p>
          </div>
        ) : (
          <div className="materials-container grid-view">
            {filteredInspirations.map((img) => (
              <div
                key={img.id}
                className={`material-card modern-material-card ${(img.archived === "true" || img.archived === true) ? 'archived' : ''}`}
                onClick={() => setSelectedMaterial(img)}
              >
                <div className="material-image-container">
                  {img.image_url ? (
                    <img
                      src={img.image_url}
                      alt={img.name}
                      className="material-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/logo192.png';
                      }}
                    />
                  ) : (
                    <div className="placeholder-image">
                      <Image size={48} />
                    </div>
                  )}
                  <div className="image-overlay">
                    <Eye size={20} />
                  </div>
                  <div className={`status-badge ${(img.archived === "true" || img.archived === true) ? 'archived' : 'available'}`}>
                    {(img.archived === "true" || img.archived === true) ? '📦 Archived' : '●'}
                  </div>

                  {/* Action buttons overlay */}
                  <div className="material-actions">
                    {!(img.archived === "true" || img.archived === true) ? (
                      <button 
                        className="action-btn archive-btn"
                        onClick={(e) => handleArchive(e, img)}
                        title="Archive Inspiration"
                      >
                        <Archive size={14} />
                      </button>
                    ) : (
                      <button 
                        className="action-btn restore-btn"
                        onClick={(e) => handleRestore(e, img)}
                        title="Restore Inspiration"
                      >
                        <RotateCcw size={14} />
                      </button>
                    )}
                    <button 
                      className="action-btn delete-btn"
                      onClick={(e) => handleDelete(e, img)}
                      title="Delete Permanently"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                
                <div className="material-info">
                  <h4>{img.name}</h4>
                  <p className="material-category">
                    {img.category === 'interior' ? '🏠 Interior' : '🏗️ Exterior'} {img.category}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`app theme-${theme}`} data-theme={theme}>
      {/* SIMPLIFIED: Only fade in/out for notifications */}
      <AnimatePresence>
        {notifications.map(notification => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.2 }} // REDUCED from 0.5s
            className={`notification notification-${notification.type}`}
            style={{ zIndex: 1000 + notification.id }}
          >
            <span>{notification.message}</span>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Clicking to remove notification:', notification.id);
                removeNotification(notification.id);
              }}
              className="notification-close-btn"
              type="button"
            >
              <X size={16} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* SIMPLIFIED: Remove header animation */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            {/* SIMPLIFIED: Remove rotating animation */}
            <div className="logo-icon">
              <img src="/ren_logo.png" alt="Renovate AI Logo" width="32" height="32" />
            </div>
            <div>
              <h1>Renovate AI</h1>
              <p className="subtitle">Material Workshop & Catalog</p>
            </div>
          </div>
          
          <div className="header-controls">
            <div className="header-nav">
              {[
                { id: 'workshop', label: 'AI Workshop', icon: Lightning },
                { id: 'catalog', label: 'Catalog', icon: Database },
                { id: 'inspiration', label: 'Inspiration Hub', icon: Sparkles }, // NEW
              ].map((tab) => (
                <button
                  key={tab.id}
                  className={`header-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
            

            
            <div className="header-stats">
              {console.log('Rendering stats:', stats)}
              <div className="stat-item">
                <Activity className="stat-icon" />
                <div>
                  <div className="stat-number">{stats.total_items || 0}</div>
                  <div className="stat-label">Total</div>
                </div>
              </div>
              <div className="stat-item">
                <TrendingUp className="stat-icon" />
                <div>
                  <div className="stat-number">{stats.catalog_images || 0}</div>
                  <div className="stat-label">Catalogue Images</div>
                </div>
              </div>
              <div className="stat-item">
                <Star className="stat-icon" />
                <div>
                  <div className="stat-number">{stats.inspiration_hub_images || 0}</div>
                  <div className="stat-label">Inspiration Hub Images</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>



      <main className="main-content">
        {/* SIMPLIFIED: Quick fade transition only */}
        <div className={`tab-content ${activeTab}`}>
          {activeTab === 'workshop' && (
            <AIWorkshop
              onGenerateImages={generateImages}
              onSaveMaterial={saveMaterial}
              onSaveInspiration={saveInspirationImage} // NEW
              isLoading={isGeneratingImages}
              showNotification={showNotification}
              generatedImages={generatedImages}
              setGeneratedImages={setGeneratedImages}
              lastGeneratedPrompt={lastGeneratedPrompt}
              setLastGeneratedPrompt={setLastGeneratedPrompt}
              currentGenerationPrompt={currentGenerationPrompt}
              setCurrentGenerationPrompt={setCurrentGenerationPrompt}
            />
          )}
          {activeTab === 'catalog' && <Catalog materials={materials} onSearch={handleSearch} onFilter={handleFilter} isLoading={isLoading} onMaterialClick={setSelectedMaterial} viewMode={viewMode} setViewMode={setViewMode} onArchive={archiveMaterial} onRestore={restoreMaterial} onDelete={deleteMaterial} filters={filters} searchQuery={searchQuery} />}
          {activeTab === 'inspiration' && <InspirationHub images={inspirationImages} isLoading={isLoading} showNotification={showNotification} setInspirationImages={setInspirationImages} loadStats={loadStats} />}
        </div>
      </main>

      {/* SIMPLIFIED: Modal with basic animation */}
      {selectedMaterial && (
        activeTab === 'inspiration' ? (
          <InspirationModal 
            inspiration={selectedMaterial} 
            onClose={() => setSelectedMaterial(null)}
            showNotification={showNotification}
            onArchive={(inspiration) => archiveInspiration(inspiration.id, inspiration.name)}
            onDelete={(inspiration) => deleteInspiration(inspiration.id, inspiration.name)}
            setInspirationImages={setInspirationImages}
          />
        ) : (
          <MaterialModal 
            material={selectedMaterial} 
            onClose={() => setSelectedMaterial(null)}
            showNotification={showNotification}
            onArchive={archiveMaterial}
            onRestore={restoreMaterial}
            onDelete={deleteMaterial}
          />
        )
      )}
    </div>
  );
};

// Dashboard component is commented out to avoid linter warnings during build.
/*
const Dashboard = ({ stats }) => (
  <div className="dashboard">
    <div className="dashboard-header">
      <h2>📊 Material Analytics Dashboard</h2>
      <p>Real-time insights into your material catalog</p>
    </div>

    <div className="dashboard-grid">
      <div className="dashboard-card modern-card">
        <div className="card-header">
          <h3>🎨 AI Workshop</h3>
          <Lightning className="card-icon" />
        </div>
        <div className="card-content">
          <div className="big-number">{stats.inspiration_hub_images || 0}</div>
          <p>Inspiration Hub Images</p>
          <div className="progress-bar">
            <div className="progress-fill gradient-cyan" style={{width: '75%'}}></div>
          </div>
          <div className="card-footer">
            <span className="trend up">+12% this week</span>
          </div>
        </div>
      </div>

      <div className="dashboard-card modern-card">
        <div className="card-header">
          <h3>📚 Material Library</h3>
          <Database className="card-icon" />
        </div>
        <div className="card-content">
          <div className="big-number">{stats.total_items || 0}</div>
          <p>Total Materials</p>
          <div className="progress-bar">
            <div className="progress-fill gradient-green" style={{width: '85%'}}></div>
          </div>
          <div className="card-footer">
            <span className="trend up">+8% this month</span>
          </div>
        </div>
      </div>

      <div className="dashboard-card modern-card">
        <div className="card-header">
          <h3>⚡ Generation Stats</h3>
          <Zap className="card-icon" />
        </div>
        <div className="card-content">
          <div className="big-number">{(stats.total_items || 0) * 4}</div>
          <p>Images Generated</p>
          <div className="progress-bar">
            <div className="progress-fill gradient-purple" style={{width: '92%'}}></div>
          </div>
          <div className="card-footer">
            <span className="trend up">+24% today</span>
          </div>
        </div>
      </div>

      <div className="dashboard-card span-full modern-card">
        <div className="card-header">
          <h3>🏷️ Category Breakdown</h3>
          <Layers className="card-icon" />
        </div>
        <div className="category-grid-modern">
          {Object.entries(stats.by_category || {}).map(([category, count]) => (
            <div key={category} className="category-item-modern">
              <div className="category-visual">
                <div className="category-icon-modern">
                  {getCategoryIcon(category)}
                </div>
              </div>
              <div className="category-info-modern">
                <div className="category-name">{category.replace('_', ' ')}</div>
                <div className="category-count">{count}</div>
                <div className="category-bar">
                  <div className="category-fill" style={{width: `${(count / Math.max(...Object.values(stats.by_category || {}))) * 100}%`}}></div>
                </div>
              </div>
            </div>
          ))}
          {Object.keys(stats.by_category || {}).length === 0 && (
            <div className="empty-state-modern">
              <Sparkles size={64} />
              <h3>Ready to Create!</h3>
              <p>Start building materials in the AI Workshop</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);
*/

// UPDATED AI Workshop Component with 2x2 Grid
const AIWorkshop = ({ onGenerateImages, onSaveMaterial, onSaveInspiration, isLoading, showNotification, generatedImages, setGeneratedImages, lastGeneratedPrompt, setLastGeneratedPrompt, currentGenerationPrompt, setCurrentGenerationPrompt }) => {
  const [formData, setFormData] = useState({
    prompt: '',
    category: '',
    subtype: '',
    keywords: '',
    num_images: 4  // Fixed at 4 images
  });
  const [materialName, setMaterialName] = useState('');

  const [isEnhancingMaterial, setIsEnhancingMaterial] = useState(false);
  const [isInspiring, setIsInspiring] = useState(false);
  // Zoom modal state
  const [selectedZoomImage, setSelectedZoomImage] = useState(null);
  const [showZoomModal, setShowZoomModal] = useState(false);
  // Test Room Modal state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testModalImage, setTestModalImage] = useState(null);
  const [apiResult, setApiResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  // New mapping for category -> subcategory (no variants)
  const CATEGORY_SUBTYPE_VARIANT_MAP = {
    flooring: [
      'Hardwood',
      'Carpet',
      'Tiles',
      'Stone'
    ],
    wall_finishes: [
      'Paint',
      'Textured Plaster',
      'Wood Paneling',
      'Wallpaper',
      'Tiles',
      'Stone Veneer / Brick',
      'Plaster / Stucco'
    ],
    cabinetry: [
      'Backsplash',
      'Handles',
      'Cabinet Finishes'
    ],
    furniture: [
      'Chair',
      'Sofa',
      'Table',
      'Bed',
      'Dresser',
      'Bookshelf',
      'Cupboard'
    ],
    textiles: [
      'Rug',
      'Pillows',
      'Curtains',
      'Throws',
      'Other Décor',
      'Art'
    ],
    lighting: [
      'Table lamp',
      'Floor Lamp',
      'Wall Sconces',
      'Pendant'
    ],
    bathroom: [
      'Floor',
      'Wall Finish',
      'Fixtures',
      'Accessories'
    ],
    // --- INSPIRATION HUB SUBTYPES ---
    interior: [
      'Bathroom',
      'Bedroom',
      'Kitchen',
      'Living Room',
      'Walk In Wardrobe'
    ],
    exterior: [
      // No subtypes for exterior currently
    ]
  };

  // Subtype options based on category
  const getSubtypeOptions = (category) => {
    const subtypes = CATEGORY_SUBTYPE_VARIANT_MAP[category] || [];
    return subtypes.map(key => ({
      label: key,
      value: key
    }));
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setFormData({...formData, category, subtype: ''});
  };

  // Handle subtype change
  const handleSubtypeChange = (subtype) => {
    setFormData({...formData, subtype});
  };

  // Enhance prompt with loading animation
  const enhancePrompt = async (type = 'material', setLoadingState) => {
    if (!formData.prompt.trim()) return;
    setLoadingState(true);
    try {
      const response = await axios.post(`${API_BASE}/api/analyze-prompt`, {
        prompt: formData.prompt,
        category: formData.category,
        enhancement_type: type
      });
      if (response.data.enhanced_prompt !== response.data.original_prompt) {
        setFormData(prev => ({ ...prev, prompt: response.data.enhanced_prompt }));
        if (type === 'inspiration') {
          showNotification('🌟 Prompt inspired for room/scene generation!', 'success');
        } else {
          showNotification('✨ Prompt enhanced for material swatch!', 'success');
        }
      } else {
        if (type === 'inspiration') {
          showNotification('💡 Your prompt is already inspiring!', 'info');
        } else {
          showNotification('💡 Your prompt is already well-optimized!', 'info');
        }
      }
    } catch (error) {
      console.error('Error enhancing prompt:', error);
      showNotification('❌ Enhancement failed. Please try again.', 'error');
    } finally {
      setLoadingState(false);
    }
  };



  // Generate material swatch images
  const handleGenerate = async () => {
    if (!formData.prompt.trim() || !formData.category) {
      showNotification('❌ Please fill in prompt and category', 'error');
      return;
    }

    try {
      // Store the current generation prompt
      setCurrentGenerationPrompt(formData.prompt);
      // Clear previously generated images before generating new ones
      setGeneratedImages([]);
      const result = await onGenerateImages(formData);
      console.log('🎨 Generated new material swatch images:', result.images);
      setGeneratedImages(result.images || []);
      showNotification('🎨 Material swatch images generated! Click any image to zoom and save.', 'success');
      setLastGeneratedPrompt(formData.prompt); // Store the prompt for display
      
      // Reset form after successful generation
      setFormData({
        prompt: '',
        category: '',
        subtype: '',
        keywords: '',
        num_images: 4
      });
    } catch (error) {
      console.error('Generation error:', error);
    }
  };



  // Save selected material (from zoom modal)
  const handleSave = async () => {
    if (!selectedZoomImage || !materialName.trim()) {
      showNotification('❌ Please enter a material name', 'error');
      return;
    }
    try {
      await onSaveMaterial({
        name: materialName,
        category: formData.category,
        subtype: formData.subtype,
        prompt: formData.prompt || lastGeneratedPrompt, // Use current prompt or last generated prompt
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
        image_url: selectedZoomImage.url
      });
      const remainingImages = generatedImages.filter(img => img.url !== selectedZoomImage.url);
      setGeneratedImages(remainingImages);
      setMaterialName('');
      setShowZoomModal(false);
      setSelectedZoomImage(null);
      // Clear the prompt only after successful save
      setFormData(prev => ({ ...prev, prompt: '' }));
      // Only clear the stored prompt if no more images remain
      if (remainingImages.length === 0) {
        setLastGeneratedPrompt(''); // Clear the stored prompt only when all images are saved
        setCurrentGenerationPrompt(''); // Clear the current generation prompt as well
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  // Handle 'Test in My Room' click in ImageZoomModal
  const handleRunTest = async (roomFile) => {
    setIsTesting(true);
    setApiResult(null);
    try {
      console.log('🧪 Test Room: Starting process for', roomFile.name);
      
      const roomImageUrl = await uploadRoomImage(roomFile);
      if (!roomImageUrl) {
        throw new Error('Failed to process room image');
      }
      
      // Ensure we have required fields and map categories for better AI detection
      let finalCategory = testModalImage.category || formData.category;
      if (!finalCategory) {
        console.log('🧪 Test Room: No category selected, using default (flooring)');
        finalCategory = 'flooring';
      }
      
      // Map internal categories to API-friendly categories for better AI detection
      const categoryMapping = {
        'flooring': 'floor',
        'wall_finishes': 'wall',
        'cabinetry': 'cabinet',
        'furniture': 'furniture',
        'textiles': 'textile',
        'lighting': 'lighting',
        'bathroom': 'bathroom',
        'interior': 'interior',
        'exterior': 'exterior'
      };
      
      const apiCategory = categoryMapping[finalCategory] || finalCategory;
      let finalMaterialName = materialName || testModalImage.name || `Test ${finalCategory}`;
      
      // Prepare clean, simple request data
      const requestData = {
        original_image_url: roomImageUrl,
        material_reference_image_url: testModalImage.url,
        material_data: {
          id: `temp_${Date.now()}`,
          name: finalMaterialName,
          category: apiCategory,
          prompt: testModalImage.prompt || ''
        }
      };

      console.log('🧪 Test Room: Sending request to AI API', {
        material: finalMaterialName,
        category: apiCategory,
        hasRoomImage: !!roomImageUrl,
        hasMaterialImage: !!testModalImage.url
      });

      // Try real API call
      try {
        const response = await fetch(`${TEST_API_BASE}${TEST_API_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': TEST_API_KEY
          },
          body: JSON.stringify(requestData)
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && data.result_image_url) {
            console.log('🧪 Test Room: AI processing successful');
            setApiResult(data.result_image_url);
            showNotification('✅ Material test completed successfully!', 'success');
            return;
          } else {
            console.log('🧪 Test Room: API returned success but no result image');
          }
        } else {
          console.log('🧪 Test Room: API error status:', response.status);
        }
      } catch (apiError) {
        console.log('🧪 Test Room: API call failed:', apiError.message);
      }
      
      // Fallback to simulation if API fails
      console.log('🧪 Test Room: Using simulation mode (API unavailable)');
      await new Promise(resolve => setTimeout(resolve, 2000));
      setApiResult(roomImageUrl);
      showNotification('🧪 Test completed (simulation mode)', 'success');
    } catch (err) {
      console.error('🧪 Test Room: Error:', err.message);
      setApiResult(null);
      showNotification('❌ Failed to process image. Please try again.', 'error');
    } finally {
      setIsTesting(false);
    }
  };

  // Helper function to upload room image and get URL
  const uploadRoomImage = async (roomFile) => {
    try {
      console.log('🧪 Test Room: Processing room image:', roomFile.name);
      
      // For now, use base64 directly since upload endpoint doesn't exist
      // and we're testing locally with CORS issues
      const base64Result = await fileToBase64(roomFile);
      
      if (!base64Result || !base64Result.startsWith('data:image/')) {
        throw new Error('Invalid image format');
      }
      
      console.log('🧪 Test Room: Room image processed successfully');
      return base64Result;
    } catch (error) {
      console.error('🧪 Test Room: Image processing error:', error.message);
      throw new Error('Failed to process room image');
    }
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="ai-workshop creation-mode">
      <div className="workshop-container">
        <div className="creation-form">
          <div className="form-section">
            <h3>🤖 AI Image Generator</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Category *</label>
                <select 
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="modern-select"
                >
                  <option value="">Choose a category...</option>
                  <option value="flooring">🏠 Flooring</option>
                  <option value="wall_finishes">🧱 Wall Finishes</option>
                  <option value="cabinetry">🗄️ Cabinetry</option>
                  <option value="furniture">🪑 Furniture</option>
                  <option value="textiles">🧵 Soft Decor & Textiles</option>
                  <option value="lighting">💡 Lighting</option>
                  <option value="bathroom">🚿 Bathroom</option>
                  <option value="exterior">🏡 Exterior Inspirations</option>
                  <option value="interior">🏠 Interior Inspirations Hub</option>
                </select>
              </div>
              <div className="form-group">
                <label>Subtype</label>
                {formData.category ? (
                  <select 
                    value={formData.subtype}
                    onChange={(e) => handleSubtypeChange(e.target.value)}
                    className="modern-select"
                  >
                    <option value="">Select subtype...</option>
                    {getSubtypeOptions(formData.category).map((subtype, index) => (
                      <option key={index} value={subtype.value}>{subtype.label}</option>
                    ))}
                  </select>
                ) : (
                  <div className="placeholder-select">
                    <span>Select a category first</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="prompt-header">
              <h3>🎨 AI Prompt *</h3>
              <div className="prompt-controls">
                <button 
                  type="button"
                  className={`enhance-button ${isEnhancingMaterial ? 'enhancing' : ''}`}
                  onClick={() => enhancePrompt('material', setIsEnhancingMaterial)}
                  disabled={!formData.prompt.trim() || isEnhancingMaterial || isInspiring || isLoading}
                >
                  {isEnhancingMaterial ? (
                    <>
                      <div className="ai-spinner" />
                      <span className="enhance-text">
                        AI Enhancing<span className="dots">
                          <span>.</span>
                          <span>.</span>
                          <span>.</span>
                        </span>
                      </span>
                    </>
                  ) : (
                    <>
                      <Wand2 size={16} />
                      Enhance
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className={`inspire-button ${isInspiring ? 'enhancing' : ''}`}
                  onClick={() => enhancePrompt('inspiration', setIsInspiring)}
                  disabled={!formData.prompt.trim() || isEnhancingMaterial || isInspiring || isLoading}
                  style={{ marginLeft: 8 }}
                >
                  {isInspiring ? (
                    <>
                      <div className="ai-spinner" />
                      <span className="enhance-text">
                        AI Inspiring<span className="dots">
                          <span>.</span>
                          <span>.</span>
                          <span>.</span>
                        </span>
                      </span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Inspire
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <textarea 
              value={formData.prompt}
              onChange={(e) => setFormData({...formData, prompt: e.target.value})}
              placeholder="Describe your material in detail... Be specific about colors, textures, style, lighting..."
              rows={4}
              className="modern-textarea"
            />


          </div>

          <div className="form-section">
            <h3>🏷️ Additional Details</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Keywords</label>
                <input 
                  type="text"
                  placeholder="modern, luxury, sustainable, natural"
                  value={formData.keywords}
                  onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                  className="modern-input"
                />
              </div>
              
            </div>
          </div>

          <button 
            className="generate-button"
            onClick={handleGenerate}
            disabled={isLoading || !formData.prompt.trim() || !formData.category}
          >
            {isLoading ? (
              <>
                <RefreshCw className="spinning" size={20} />
                Generating...
              </>
            ) : (
              <>
                <Lightning size={20} />
                Generate with AI
              </>
            )}
          </button>
        </div>
        <div className="ai-workshop-main">
          <div className="preview-panel">
            <h3>🔮 AI Preview</h3>
            <div className="preview-content">
              {(formData.prompt || currentGenerationPrompt || lastGeneratedPrompt || generatedImages.length > 0) ? (
                <div className="prompt-preview">
                  <p className="prompt-text">"{formData.prompt || currentGenerationPrompt || lastGeneratedPrompt}"</p>
                </div>
              ) : (
                <div className="preview-placeholder">
                  <div className="preview-star-center">
                    <Sparkles size={28} />
                  </div>
                  <p>Start typing to see your prompt preview</p>
                </div>
              )}
            </div>
          </div>
          {/* New container for generated images below preview */}
          {generatedImages.length > 0 && (
            <div className="generated-images-section">
              <h4>🎨 Generated Images</h4>
              <div className="preview-images-grid">
                {generatedImages.map((img, idx) => {
                  // Determine service based on filename
                  const isDalle = img.url.includes('dalle_');
                  const isGptImage = img.url.includes('gpt_image_');
                  const isGemini = img.url.includes('gemini_');
                  const service = isDalle ? 'DALL-E' : isGptImage ? 'GPT-Image-1' : isGemini ? 'Gemini' : 'Unknown';
                  
                  return (
                    <div
                      key={img.url}
                      className="preview-image-card"
                      onClick={() => {
                        setSelectedZoomImage(img);
                        setShowZoomModal(true);
                        setMaterialName('');
                      }}
                    >
                      <div className="preview-image-container">
                        <img src={img.url} alt={`Generated ${idx + 1}`} className="preview-image" />
                        <div className="preview-image-index">{idx + 1}</div>
                        <div className={`service-badge ${isDalle ? 'dalle' : isGptImage ? 'gpt-image-1' : isGemini ? 'gemini' : 'unknown'}`}>
                          {service}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="click-hint">💡 Click any image to zoom and save</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Zoom Modal */}
      {showZoomModal && selectedZoomImage && (
        <ImageZoomModal 
          image={selectedZoomImage}
          imageIndex={generatedImages.findIndex(img => img.url === selectedZoomImage.url) + 1}
          onClose={() => {
            setShowZoomModal(false);
            setSelectedZoomImage(null);
          }}
          onSave={handleSave}
          materialName={materialName}
          setMaterialName={setMaterialName}
          isLoading={isLoading}
          setShowTestModal={setShowTestModal}
          setTestModalImage={setTestModalImage}
          setApiResult={setApiResult}
          setIsTesting={setIsTesting}
          handleRunTest={handleRunTest}
          onSaveInspiration={onSaveInspiration}
          setGeneratedImages={setGeneratedImages}
          formData={formData}
        />
      )}

      {/* Test Room Modal */}
      {showTestModal && (
        <TestRoomModal
          open={showTestModal}
          onClose={() => {
            console.log("canceled run");
            setShowTestModal(false);
            setIsTesting(false); // Reset testing state when modal is closed
          }}
          materialImage={testModalImage}
          apiResult={apiResult}
          setApiResult={setApiResult}
          isTesting={isTesting}
          onRunTest={handleRunTest}
          onBackToImageModal={() => {
            console.log("canceled run");
            setShowTestModal(false);
            setIsTesting(false); // Reset testing state when going back to image modal
          }}
        />
      )}
    </div>
  );
};

// Add TestRoomModal component
const TestRoomModal = ({ open, onClose, materialImage, apiResult, setApiResult, isTesting, onRunTest, onBackToImageModal }) => {
  const [roomFile, setRoomFile] = useState(null);
  const [roomPreview, setRoomPreview] = useState(null);

  useEffect(() => {
    if (!open) {
      setRoomFile(null);
      setRoomPreview(null);
      setApiResult(null);
    }
  }, [open, setApiResult]);

  const handleRoomFileChange = (e) => {
    const file = e.target.files[0];
    setRoomFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setRoomPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setRoomPreview(null);
    }
  };

  if (!open) return null;

  return (
    <div className={`modal-overlay open`} onClick={onBackToImageModal || onClose}>
      <div className="modal-content test-room-modal test-room-modal-horizontal" onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onBackToImageModal || onClose} type="button">✖</button>
        <h2>🧪 Test Material in Your Room</h2>
        <div className="test-room-horizontal-row">
          <div className="test-room-horiz-col">
            <div className="test-room-label">Material</div>
            {materialImage?.url ? (
              <img src={materialImage.url} alt="Material" className="test-room-horiz-image" />
            ) : (
              <div className="test-room-placeholder">No material selected</div>
            )}
          </div>
          <div className="test-room-horiz-col">
            <div className="test-room-label">Your Room</div>
            {roomPreview ? (
              <img src={roomPreview} alt="Room Preview" className="test-room-horiz-image" />
            ) : (
              <div className="test-room-placeholder">Upload your room image</div>
            )}
          </div>
          <div className="test-room-horiz-col">
            <div className="test-room-label">Result</div>
            {apiResult ? (
              <img 
                src={apiResult} 
                alt="Result" 
                className="test-room-horiz-image" 
                onClick={() => {
                  if (apiResult.startsWith('data:')) {
                    // For base64 images, create a blob URL
                    fetch(apiResult)
                      .then(res => res.blob())
                      .then(blob => {
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                        // Clean up the blob URL after a delay
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      })
                      .catch(err => console.error('Error opening image:', err));
                  } else {
                    // For regular URLs, open directly
                    window.open(apiResult, '_blank');
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <div className="test-room-placeholder">Result will appear here</div>
            )}
          </div>
        </div>
        <div className="test-modal-controls">
          <label className="choose-image-btn">
            <input type="file" accept="image/*" onChange={handleRoomFileChange} style={{ display: 'none' }} />
            {roomFile ? 'Change Room Image' : 'Choose Room Image'}
          </label>
        </div>
        <button
          className="run-test-btn"
          onClick={() => onRunTest(roomFile)}
          disabled={!roomFile || isTesting}
        >
          {isTesting ? 'Processing...' : 'Run Test'}
        </button>
        <div style={{ 
          marginTop: '8px', 
          fontSize: '0.8rem', 
          color: 'var(--text-secondary)', 
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          ⏱️ Processing may take up to 3 minutes
        </div>
      </div>
    </div>
  );
};

// Image Zoom Modal Component
const ImageZoomModal = ({ image, imageIndex, onClose, onSave, materialName, setMaterialName, isLoading, setShowTestModal, setTestModalImage, setApiResult, setIsTesting, handleRunTest, onSaveInspiration, setGeneratedImages, formData }) => {
  // Placeholder for the API call
  // This function is now passed down from AIWorkshop
  // const handleRunTest = async (roomFile) => {
  //   setIsTesting(true);
  //   setApiResult(null);
  //   try {
  //     // TODO: Replace with your real API endpoint and key
  //     // Example: const API_KEY = 'YOUR_API_KEY_HERE';
  //     // const API_URL = 'https://your-api-endpoint.com/apply-material';
  //     // const formData = new FormData();
  //     // formData.append('room', roomFile);
  //     // formData.append('material', image.url);
  //     // formData.append('api_key', API_KEY);
  //     // const response = await fetch(API_URL, {
  //     //   method: 'POST',
  //     //   body: formData
  //     // });
  //     // const result = await response.json();
  //     // setApiResult(result);
  //     // setTestModalImage(image.url);
  //     // setShowTestModal(true);
  //   } catch (error) {
  //     console.error('Error running test:', error);
  //     setApiResult({ error: 'Failed to run test' });
  //   } finally {
  //     setIsTesting(false);
  //   }
  // };

  return (
    <div className="image-zoom-overlay" onClick={onClose}>
      <div className="image-zoom-modal" onClick={e => e.stopPropagation()} style={{ minHeight: '650px', padding: '28px', maxWidth: '850px', width: '88vw' }}>
        <div className="zoom-modal-body">
          <div className="zoom-image-container" style={{ position: 'relative', marginBottom: '20px' }}>
            <button className="zoom-close-button" onClick={onClose} style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>✖</button>
            {image?.url ? (
              <img 
                src={image.url} 
                alt="Zoomed" 
                className="zoom-image" 
                style={{ 
                  borderRadius: '12px', 
                  boxShadow: 'var(--shadow-lg)', 
                  maxHeight: '300px'
                }}
              />
            ) : (
              <div className="placeholder-image" style={{ 
                borderRadius: '12px', 
                boxShadow: 'var(--shadow-lg)', 
                maxHeight: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--bg-secondary)'
              }}>
                <Image size={48} />
              </div>
            )}
          </div>
          <form className="zoom-save-form" onSubmit={e => { e.preventDefault(); onSave(); }} style={{ padding: '0 12px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '1.5rem', color: 'var(--text-primary)', fontWeight: '600' }}>Save to Catalogue</h4>
            <input
              type="text"
              value={materialName}
              onChange={e => setMaterialName(e.target.value)}
              placeholder="Enter material name..."
              className="zoom-material-input"
              required
              style={{ marginBottom: '16px', fontSize: '1rem', padding: '12px 16px' }}
            />
            <div className="zoom-modal-button-row" style={{ gap: '12px', marginTop: '8px', paddingBottom: '16px' }}>
              <button
                type="submit"
                className="zoom-save-button"
                disabled={isLoading || !materialName.trim()}
                style={{ 
                  minHeight: '48px', 
                  fontSize: '1rem',
                  flex: '1'
                }}
              >
                {isLoading ? 'Saving...' : '💾 Save to Catalogue'}
              </button>
              <button
                type="button"
                className="test-btn"
                onClick={() => {
                  setShowTestModal(true);
                  setTestModalImage(image);
                }}
                style={{ 
                  minHeight: '48px', 
                  fontSize: '1rem',
                  flex: '1'
                }}
              >
                🧪 Test in My Room
              </button>
              <button
                type="button"
                className="zoom-save-button"
                onClick={async () => {
                  // DEBUG: Log the data being saved to inspiration hub
                  const inspirationData = {
                    name: materialName,
                    prompt: image.prompt,
                    category: image.category,
                    subcategory: image.subcategory || "",  // Use image.subcategory since it's now enhanced
                    tags: image.tags || [],  // Use image.tags since they're now enhanced
                    image_url: image.url
                  };
                  console.log('🐛 DEBUG: Saving inspiration with data:', JSON.stringify(inspirationData, null, 2));
                  console.log('🐛 DEBUG: Original image object:', JSON.stringify(image, null, 2));
                  console.log('🐛 DEBUG: Form data subcategory:', formData?.subtype);
                  
                  await onSaveInspiration(inspirationData);
                  setGeneratedImages && setGeneratedImages(prev => prev.filter(img => img.url !== image.url));
                  onClose();
                }}
                disabled={isLoading || !materialName.trim()}
                style={{ 
                  minHeight: '48px', 
                  fontSize: '1rem',
                  flex: '1',
                  background: 'linear-gradient(135deg, var(--secondary), var(--accent))'
                }}
              >
                ✨ Save to Inspiration Hub
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// UPDATED Catalog Component with Archive/Restore/Delete functionality
const Catalog = ({ materials, onSearch, onFilter, isLoading, onMaterialClick, viewMode, setViewMode, onArchive, onRestore, onDelete, filters, searchQuery: propSearchQuery }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [localFilters, setLocalFilters] = useState({
    category: '',
    status: ''
  });

  // Sync local state with props when they change
  useEffect(() => {
    if (filters) {
      setLocalFilters({
        category: filters.category || '',
        status: filters.status || ''
      });
    }
  }, [filters]);

  // Sync searchQuery with prop
  useEffect(() => {
    setSearchQuery(propSearchQuery || '');
  }, [propSearchQuery]);
  const [sortBy, setSortBy] = useState('newest');

  const handleSearch = (value) => {
    setSearchQuery(value);
    onSearch(value);
  };

  // Function to show all materials
  const showAllMaterials = () => {
    setLocalFilters({ category: '', status: '' });
    setSearchQuery('');
    onFilter({ category: '', status: '' });
    onSearch('');
  };

  const handleFilterChange = (key, value) => {
    if ((key === 'category' || key === 'status') && value === '') {
      showAllMaterials();
      return;
    }
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilter(newFilters);
  };

  const handleClearFilters = showAllMaterials;

  // Sort and filter materials
  const normalizedMaterials = materials.map(m => ({
    ...m,
    status: m.status === 'active' ? 'available' : m.status
  }));
  const sortedMaterials = React.useMemo(() => {
    const arr = [...normalizedMaterials];
    if (sortBy === 'newest') {
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'oldest') {
      arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === 'name') {
      arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'category') {
      arr.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
    }
    return arr;
  }, [normalizedMaterials, sortBy]);
  
  const filteredMaterials = sortedMaterials.filter(m => {
    // Show all if status is ''
    const statusMatch = localFilters.status === '' ||
      (localFilters.status === 'available' && m.status !== 'archived') ||
      (localFilters.status === 'archived' && m.status === 'archived');
    
    // Show all if category is ''
    const categoryMatch = localFilters.category === '' || m.category === localFilters.category;
    // Search filter
    const searchMatch = searchQuery === '' ||
      (m?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m?.keywords || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m?.sku || '').toLowerCase().includes(searchQuery.toLowerCase());
    return statusMatch && categoryMatch && searchMatch;
  });

  // NEW: Handle archive action with confirmation
  const handleArchive = async (e, material) => {
    e.stopPropagation(); // Prevent opening modal
    
    if (window.confirm(`Are you sure you want to archive "${material.name}"?`)) {
      try {
        await onArchive(material.id, material.name);
      } catch (error) {
        console.error('Archive failed:', error);
      }
    }
  };

  // NEW: Handle restore action
  const handleRestore = async (e, material) => {
    e.stopPropagation(); // Prevent opening modal
    
    try {
      await onRestore(material.id, material.name);
    } catch (error) {
      console.error('Restore failed:', error);
    }
  };

  // NEW: Handle delete action with confirmation
  const handleDelete = async (e, material) => {
    e.stopPropagation(); // Prevent opening modal
    
    if (window.confirm(`⚠️ Are you sure you want to PERMANENTLY DELETE "${material.name}"? This action cannot be undone!`)) {
      try {
        await onDelete(material.id, material.name);
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  return (
    <div className="catalog modern-catalog">
      <div className="catalog-header">
        <div className="catalog-title">
          <h2>📚 Material Catalog</h2>
          <p>Browse and manage your AI-generated materials</p>
        </div>
        
        <div className="catalog-controls">
          <div className="search-section">
            <div className="search-bar modern-search">
              <Search className="search-icon" />
              <input 
                type="text"
                placeholder="Search materials, keywords, SKUs..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          
          <div className="filter-section">
            <select 
              value={localFilters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="modern-filter"
            >
              <option value="">All Categories</option>
              <option value="flooring">🏠 Flooring</option>
              <option value="wall_finishes">🧱 Wall Finishes</option>
              <option value="cabinetry">🗄️ Cabinetry</option>
              <option value="furniture">🪑 Furniture</option>
              <option value="textiles">🧵 Soft Decor & Textiles</option>
              <option value="lighting">💡 Lighting</option>
              <option value="bathroom">🚿 Bathroom</option>
              <option value="exterior">🏗️ Exterior</option>
            </select>

            <select 
              value={localFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="modern-filter"
            >
              <option value="">All Status</option>
              <option value="available">✅ Available</option>
              <option value="archived">📦 Archived</option>
            </select>
            
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="modern-filter"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">Name A-Z</option>
              <option value="category">Category</option>
            </select>

            {/* Clear Filters Button */}
            {(localFilters.category || localFilters.status || searchQuery) && (
              <button
                className="modern-filter"
                style={{ minWidth: 100, background: '#eee', color: '#333', marginLeft: 8 }}
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner-large" />
          <p>Loading materials...</p>
        </div>
      ) : filteredMaterials.length === 0 ? (
        <div className="empty-state-modern">
          <Database size={64} />
          <h3>No materials found</h3>
          <p>Start creating in the AI Workshop!</p>
        </div>
      ) : (
        <div className={`materials-container ${viewMode}-view`}>
          {filteredMaterials.map((material) => (
            <div
              key={material.id}
              className={`material-card modern-material-card ${material.status === 'archived' ? 'archived' : ''}`}
              onClick={() => onMaterialClick(material)}
            >
              <div className="material-image-container">
                {material.images && material.images.length > 0 ? (
                  <img 
                    src={material.images[0].url && material.images[0].url.includes('serve-image') 
                      ? material.images[0].url
                      : material.images[0].url && material.images[0].url.includes('r2.dev') 
                        ? `https://renovate-library-service-jo572.ondigitalocean.app/api/proxy-image?url=${encodeURIComponent(material.images[0].url)}`
                        : material.images[0].url
                    } 
                    alt={material.name}
                    className="material-image"
                    onError={(e) => {
                      e.target.src = 'https://picsum.photos/400/200?random=' + Math.random();
                    }}
                  />
                ) : (
                  <div className="placeholder-image">
                    <Image size={48} />
                  </div>
                )}
                <div className="image-overlay">
                  <Eye size={20} />
                </div>
                <div className={`status-badge ${material.status}`}>
                  {material.status === 'archived' ? '📦 Archived' : '●'}
                </div>

                {/* NEW: Action buttons overlay */}
                <div className="material-actions">
                  {material.status === 'available' ? (
                    <button 
                      className="action-btn archive-btn"
                      onClick={(e) => handleArchive(e, material)}
                      title="Archive Material"
                    >
                      <Archive size={14} />
                    </button>
                  ) : (
                    <button 
                      className="action-btn restore-btn"
                      onClick={(e) => handleRestore(e, material)}
                      title="Restore Material"
                    >
                      <RotateCcw size={14} />
                    </button>
                  )}
                  <button 
                    className="action-btn delete-btn"
                    onClick={(e) => handleDelete(e, material)}
                    title="Delete Permanently"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              
              <div className="material-info">
                <h4>{material.name}</h4>
                <p className="material-category">
                  {getCategoryIcon(material.category)} {material.category}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// UPDATED Material Modal with Archive/Restore/Delete functionality
const MaterialModal = ({ material, onClose, showNotification, onArchive, onRestore, onDelete }) => {
  // Prevent background scroll when modal is open
  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Zoom state
  const [zoomed, setZoomed] = React.useState(false);
  
  const handleExport = async () => {
    if (!material.images || material.images.length === 0) {
      showNotification('❌ No images to export', 'error');
      return;
    }

    try {
      showNotification('📥 Exporting images...', 'info');
      
      // Export each image
      for (let i = 0; i < material.images.length; i++) {
        const image = material.images[i];
        const imageUrl = image.url && image.url.includes('serve-image') 
          ? image.url
          : image.url && image.url.includes('r2.dev') 
            ? `https://renovate-library-service-jo572.ondigitalocean.app/api/proxy-image?url=${encodeURIComponent(image.url)}`
            : image.url;
        
        // Fetch the image
        const response = await fetch(imageUrl);
        if (!response.ok) {
          console.error(`Failed to fetch image ${i + 1}:`, response.statusText);
          continue;
        }
        
        const blob = await response.blob();
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Determine file extension based on content type
        let extension = 'png';
        if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
          extension = 'jpg';
        } else if (blob.type.includes('webp')) {
          extension = 'webp';
        }
        
        // Create filename
        const safeName = material.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        link.download = `${safeName}_image_${i + 1}.${extension}`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      showNotification(`💾 Exported ${material.images.length} image(s) successfully!`, 'success');
    } catch (error) {
      console.error('Export failed:', error);
      showNotification('❌ Failed to export images', 'error');
    }
  };

  const handleCopyDetails = async () => {
    const detailsText = `
Material: ${material.name}
SKU: ${material.sku}
Category: ${material.category}
${material.subtype ? `Subtype: ${material.subtype}` : ''}
${material.prompt ? `AI Prompt: "${material.prompt}"` : ''}
${material.keywords && material.keywords.length > 0 ? `Keywords: ${material.keywords.join(', ')}` : ''}
Created: ${new Date(material.created_at).toLocaleDateString()}
Source: ${material.source}
Status: ${material.status}
    `.trim();

    try {
      await navigator.clipboard.writeText(detailsText);
      showNotification('📋 Material details copied to clipboard!', 'success');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = detailsText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showNotification('📋 Material details copied to clipboard!', 'success');
    }
  };



  // NEW: Handle archive from modal
  const handleArchiveFromModal = async () => {
    if (window.confirm(`Are you sure you want to archive "${material.name}"?`)) {
      try {
        await onArchive(material.id, material.name);
        onClose(); // Close modal after action
      } catch (error) {
        console.error('Archive failed:', error);
      }
    }
  };

  // NEW: Handle restore from modal
  const handleRestoreFromModal = async () => {
    try {
      await onRestore(material.id, material.name);
      onClose(); // Close modal after action
    } catch (error) {
      console.error('Restore failed:', error);
    }
  };



  // NEW: Handle delete from modal
  const handleDeleteFromModal = async () => {
    if (window.confirm(`⚠️ Are you sure you want to PERMANENTLY DELETE "${material.name}"? This action cannot be undone!`)) {
      try {
        await onDelete(material.id, material.name);
        onClose(); // Close modal after action
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  // ... existing code ...

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modern-modal modal-content-status-close-parent" onClick={(e) => e.stopPropagation()}>
        <div className="modal-status-badge-floating">
          <span className={`status-badge ${material.status}`}>
            {material.status === 'archived' ? 'Archived' : 'Available'}
          </span>
        </div>
        <div className="modal-close-floating">
          <button onClick={onClose} className="close-button close-button-floating">
            <X size={24} />
          </button>
        </div>
        <div className="modal-body">
          <div className="material-modal-horizontal-row">
            {/* Left: Main Image and Prompt */}
            <div className="material-modal-left">
              <div className="material-image-section">
                {material.images && material.images.length > 0 && (
                  <img
                    src={material.images[0].url && material.images[0].url.includes('serve-image')
                      ? material.images[0].url
                      : material.images[0].url && material.images[0].url.includes('r2.dev')
                        ? `https://renovate-library-service-jo572.ondigitalocean.app/api/proxy-image?url=${encodeURIComponent(material.images[0].url)}`
                        : material.images[0].url
                    }
                    alt={`${material.name} 1`}
                    className="material-modal-main-image"
                    onError={(e) => {
                      e.target.src = 'https://picsum.photos/300/200?random=' + Math.random();
                    }}
                    style={{ cursor: 'zoom-in' }}
                    onClick={() => setZoomed(true)}
                  />
                )}
              </div>
              {material.prompt && (
                <div className="material-prompt-section">
                  <h4>🎨 AI Prompt</h4>
                  <div className="material-prompt-display">
                    "{material.prompt}"
                  </div>
                </div>
              )}
            </div>
            {/* Right: Details */}
            <div className="material-modal-right">
              <div className="material-details">
                {/* Material Title and Category */}
                <div className="material-title-section">
                  <h2 className="material-title">{material.name}</h2>
                  <span className="category-badge">
                    {getCategoryIcon(material.category)} {material.category}
                  </span>
                </div>
                
                <div className="detail-section">
                  <h4>📋 Material Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">SKU:</span>
                      <span className="value">{material.sku}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Category:</span>
                      <span className="value">{material.category}</span>
                    </div>
                    {material.subtype && (
                      <div className="detail-item">
                        <span className="label">Subtype:</span>
                        <span className="value">{material.subtype}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="label">Created:</span>
                      <span className="value">{new Date(material.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Source:</span>
                      <span className="value">{material.source}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className="value">{material.status}</span>
                    </div>
                  </div>
                </div>

                {material.keywords && material.keywords.length > 0 && (
                  <div className="keywords-display">
                    {material.keywords.map((kw, i) => (
                      <span className="keyword-tag" key={i}>{kw}</span>
                    ))}
                  </div>
                )}
                <div className="modal-management-actions">
                  <button className="action-btn primary" onClick={handleCopyDetails}>Copy Details</button>
                  <button className="action-btn secondary" onClick={handleExport}>Export Images</button>
                  {material.status === 'available' && (
                    <button className="action-btn warning" onClick={handleArchiveFromModal}>Archive</button>
                  )}
                  {material.status === 'archived' && (
                    <button className="action-btn success" onClick={handleRestoreFromModal}>Restore</button>
                  )}
                  <button className="action-btn danger" onClick={handleDeleteFromModal}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Zoomed Image Overlay */}
      {zoomed && material.images && material.images.length > 0 && (
        <div className="material-modal-zoom-overlay" onClick={() => setZoomed(false)}>
          <img
            src={material.images[0].url && material.images[0].url.includes('serve-image')
              ? material.images[0].url
              : material.images[0].url && material.images[0].url.includes('r2.dev')
                ? `https://renovate-library-service-jo572.ondigitalocean.app/api/proxy-image?url=${encodeURIComponent(material.images[0].url)}`
                : material.images[0].url
            }
            alt={`${material.name} zoomed`}
            className="material-modal-zoomed-image"
            onClick={e => e.stopPropagation()}
          />
          <button className="material-modal-zoom-close" onClick={() => setZoomed(false)} title="Close zoom">✖</button>
        </div>
      )}
    </div>
  );
};

// Inspiration Modal Component - Matches Catalog Layout
const InspirationModal = ({ inspiration, onClose, showNotification, onArchive, onDelete, setInspirationImages }) => {
  // Prevent background scroll when modal is open
  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Zoom state
  const [zoomed, setZoomed] = React.useState(false);
  
  // Color tag management state
  const [showColorTagEditor, setShowColorTagEditor] = React.useState(false);
  const [selectedColorTag, setSelectedColorTag] = React.useState('');
  
  // Local state to track the current inspiration data (including updates)
  const [currentInspiration, setCurrentInspiration] = React.useState(inspiration);
  
  // Update local state when prop changes
  React.useEffect(() => {
    setCurrentInspiration(inspiration);
  }, [inspiration]);
  
  // Available color tags from backend
  const availableColorTags = [
    "blacks", "whites", "grays", "blues", "greens", "reds", "yellows", "oranges", "purples", "browns", "neutrals",
    "pastels", "gold", "silver", "pinks"
  ];
  
  const handleExport = async () => {
    if (!currentInspiration.image_url) {
      showNotification('❌ No image to export', 'error');
      return;
    }

    try {
      showNotification('📥 Exporting image...', 'info');
      
      const imageUrl = currentInspiration.image_url && currentInspiration.image_url.includes('serve-image') 
        ? currentInspiration.image_url
        : currentInspiration.image_url && currentInspiration.image_url.includes('r2.dev') 
          ? `https://renovate-library-service-jo572.ondigitalocean.app/api/proxy-image?url=${encodeURIComponent(currentInspiration.image_url)}`
          : currentInspiration.image_url;
      
      // Fetch the image
      const response = await fetch(imageUrl);
      if (!response.ok) {
        console.error('Failed to fetch image:', response.statusText);
        showNotification('❌ Failed to fetch image', 'error');
        return;
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Determine file extension based on content type
      let extension = 'png';
      if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
        extension = 'jpg';
      } else if (blob.type.includes('webp')) {
        extension = 'webp';
      }
      
      // Create filename
      const safeName = currentInspiration.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `${safeName}_inspiration.${extension}`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showNotification('💾 Image exported successfully!', 'success');
    } catch (error) {
      console.error('Export failed:', error);
      showNotification('❌ Failed to export image', 'error');
    }
  };

  const handleCopyDetails = async () => {
    const detailsText = `
Inspiration: ${currentInspiration.name}
Category: ${currentInspiration.category}
${currentInspiration.subcategory ? `Subcategory: ${currentInspiration.subcategory}` : ''}
${currentInspiration.prompt ? `AI Prompt: "${currentInspiration.prompt}"` : ''}
${currentInspiration.tags && currentInspiration.tags.length > 0 ? `Tags: ${currentInspiration.tags.join(', ')}` : ''}
${currentInspiration.color_tags && currentInspiration.color_tags.length > 0 ? `Color Tags: ${currentInspiration.color_tags.join(', ')}` : ''}
Created: ${new Date(currentInspiration.created_at).toLocaleDateString()}
Created by: ${currentInspiration.created_by || 'Unknown'}
    `.trim();

    try {
      await navigator.clipboard.writeText(detailsText);
      showNotification('📋 Inspiration details copied to clipboard!', 'success');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = detailsText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showNotification('📋 Inspiration details copied to clipboard!', 'success');
    }
  };



  // Handle archive from modal
  const handleArchiveFromModal = async () => {
    if (window.confirm(`Are you sure you want to archive "${currentInspiration.name}"?`)) {
      try {
        await onArchive(currentInspiration);
        onClose(); // Close modal after action
      } catch (error) {
        console.error('Archive failed:', error);
      }
    }
  };



  // ... existing code ...

  // Handle delete from modal
  const handleDeleteFromModal = async () => {
    if (window.confirm(`⚠️ Are you sure you want to PERMANENTLY DELETE "${currentInspiration.name}"? This action cannot be undone!`)) {
      try {
        await onDelete(currentInspiration);
        onClose(); // Close modal after action
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  // Color tag management functions
  const handleAddColorTag = async () => {
    if (!selectedColorTag) {
      showNotification('Please select a color tag to add', 'error');
      return;
    }

    try {
      // Ensure color_tags is always an array
      const currentColorTags = Array.isArray(currentInspiration.color_tags) ? currentInspiration.color_tags : [];
      
      if (currentColorTags.includes(selectedColorTag)) {
        showNotification('This color tag is already assigned', 'info');
        return;
      }

      const updatedColorTags = [...currentColorTags, selectedColorTag];
      console.log('Adding color tag:', selectedColorTag, 'to:', updatedColorTags);
      
      const response = await fetch(`${API_BASE}/api/inspiration-hub/images/${currentInspiration.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          color_tags: updatedColorTags.join(',')
        })
      });

      if (response.ok) {
        // Update local state in modal
        setCurrentInspiration(prev => ({
          ...prev,
          color_tags: updatedColorTags
        }));
        
        // Update global state
        setInspirationImages(prev => prev.map(img => 
          img.id === currentInspiration.id 
            ? { ...img, color_tags: updatedColorTags }
            : img
        ));
        showNotification(`Added color tag: ${selectedColorTag}`, 'success');
        setSelectedColorTag('');
        setShowColorTagEditor(false);
      } else {
        const errorData = await response.text();
        console.error('Server error:', errorData);
        showNotification('Failed to add color tag', 'error');
      }
    } catch (error) {
      console.error('Error adding color tag:', error);
      showNotification('Failed to add color tag', 'error');
    }
  };

  const handleRemoveColorTag = async (colorTagToRemove) => {
    try {
      // Ensure color_tags is always an array
      const currentColorTags = Array.isArray(currentInspiration.color_tags) ? currentInspiration.color_tags : [];
      const updatedColorTags = currentColorTags.filter(tag => tag !== colorTagToRemove);
      
      console.log('Removing color tag:', colorTagToRemove, 'from:', currentColorTags, 'result:', updatedColorTags);
      
      const response = await fetch(`${API_BASE}/api/inspiration-hub/images/${currentInspiration.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          color_tags: updatedColorTags.join(',')
        })
      });

      if (response.ok) {
        // Update local state in modal
        setCurrentInspiration(prev => ({
          ...prev,
          color_tags: updatedColorTags
        }));
        
        // Update global state
        setInspirationImages(prev => prev.map(img => 
          img.id === currentInspiration.id 
            ? { ...img, color_tags: updatedColorTags }
            : img
        ));
        showNotification(`Removed color tag: ${colorTagToRemove}`, 'success');
      } else {
        const errorData = await response.text();
        console.error('Server error:', errorData);
        showNotification('Failed to remove color tag', 'error');
      }
    } catch (error) {
      console.error('Error removing color tag:', error);
      showNotification('Failed to remove color tag', 'error');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modern-modal modal-content-status-close-parent" onClick={(e) => e.stopPropagation()}>
        <div className="modal-status-badge-floating">
          <span className={`status-badge ${currentInspiration.archived ? 'archived' : 'available'}`}>
            {currentInspiration.archived ? 'Archived' : 'Available'}
          </span>
        </div>
        <div className="modal-close-floating">
          <button onClick={onClose} className="close-button close-button-floating">
            <X size={24} />
          </button>
        </div>
        <div className="modal-body">
          <div className="material-modal-horizontal-row">
            {/* Left: Details */}
            <div className="material-modal-left">
              <div className="material-details">
                {/* Compact Header with Title and Info */}
                <div className="material-title-section compact">
                  <h2 className="material-title">{currentInspiration.name}</h2>
                  <div className="title-meta">
                    <span className="category-badge">
                      {currentInspiration.category === 'interior' ? '🏠' : '🏗️'} {currentInspiration.category}
                    </span>
                    {currentInspiration.subcategory && (
                      <span className="subcategory-badge">{currentInspiration.subcategory}</span>
                    )}
                    <span className="date-badge">{new Date(currentInspiration.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {/* Combined Tags Section */}
                <div className="detail-section compact">
                  <div className="tags-header">
                    <h4>🏷️ Tags & Colors</h4>
                    <button 
                      className="action-btn secondary small"
                      onClick={() => {
                        console.log('Current inspiration:', currentInspiration);
                        console.log('Current color_tags:', currentInspiration.color_tags);
                        setShowColorTagEditor(!showColorTagEditor);
                      }}
                    >
                      {showColorTagEditor ? 'Cancel' : '+ Color Tag'}
                    </button>
                  </div>
                  
                  {/* Color Tag Editor */}
                  {showColorTagEditor && (
                    <div className="color-tag-editor compact">
                      <select 
                        value={selectedColorTag}
                        onChange={(e) => setSelectedColorTag(e.target.value)}
                        className="color-tag-select"
                      >
                        <option value="">Select color tag...</option>
                        {availableColorTags.map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                      </select>
                      <button 
                        className="action-btn primary small"
                        onClick={handleAddColorTag}
                        disabled={!selectedColorTag}
                      >
                        Add
                      </button>
                    </div>
                  )}
                  
                  {/* Tags Display */}
                  <div className="tags-container">
                    {currentInspiration.tags && currentInspiration.tags.length > 0 && (
                      <div className="tag-group">
                        <span className="tag-label">Tags:</span>
                        <div className="keywords-display compact">
                          {currentInspiration.tags.map((tag, i) => (
                            <span className="keyword-tag" key={i}>{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {currentInspiration.color_tags && currentInspiration.color_tags.length > 0 && (
                      <div className="tag-group">
                        <span className="tag-label">Colors:</span>
                        <div className="keywords-display compact">
                          {currentInspiration.color_tags.map((colorTag, i) => (
                            <span className="keyword-tag color-tag" key={i}>
                              {colorTag}
                              <button 
                                onClick={() => handleRemoveColorTag(colorTag)}
                                className="tag-remove-btn"
                                title="Remove color tag"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Compact Action Buttons */}
                <div className="modal-management-actions compact">
                  <div className="action-row">
                    <button className="action-btn primary small" onClick={handleCopyDetails}>Copy</button>
                    <button className="action-btn secondary small" onClick={handleExport}>Export</button>
                  </div>
                  <div className="action-row">
                    {!currentInspiration.archived && (
                      <button className="action-btn warning small" onClick={handleArchiveFromModal}>Archive</button>
                    )}
                    <button className="action-btn danger small" onClick={handleDeleteFromModal}>Delete</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Main Image and Prompt */}
            <div className="material-modal-right">
              <div className="material-image-section">
                {currentInspiration.image_url && (
                  <img
                    src={currentInspiration.image_url}
                    alt={currentInspiration.name}
                    className="material-modal-main-image"
                    onError={(e) => {
                      e.target.src = 'https://picsum.photos/300/200?random=' + Math.random();
                    }}
                    style={{ cursor: 'zoom-in' }}
                    onClick={() => setZoomed(true)}
                  />
                )}
              </div>
              {currentInspiration.prompt && (
                <div className="material-prompt-section">
                  <h4>🎨 AI Prompt</h4>
                  <div className="material-prompt-display">
                    "{currentInspiration.prompt}"
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Zoomed Image Overlay */}
      {zoomed && currentInspiration.image_url && (
        <div className="material-modal-zoom-overlay" onClick={() => setZoomed(false)}>
          <img
            src={currentInspiration.image_url}
            alt={`${currentInspiration.name} zoomed`}
            className="material-modal-zoomed-image"
            onClick={e => e.stopPropagation()}
          />
          <button className="material-modal-zoom-close" onClick={() => setZoomed(false)} title="Close zoom">✖</button>
        </div>
      )}
    </div>
  );
};

// Helper functions
const getCategoryIcon = (category) => {
  const icons = {
    flooring: '🏠',
    furniture: '🪑',
    lighting: '💡',
    textiles: '🧵',
    cabinetry: '🗄️',
    bathroom: '🚿',
    exterior: '🏗️',
    wall_finishes: '🧱'
  };
  return icons[category] || '📦';
};

export default RenovateAI;